/**
 * Talent Profile (Client Portal) — port pixel-perfect del handoff
 * `scouting-wizard-client-portal/project/Talent Profile.html` (SW-140).
 *
 * Estructura: breadcrumb + hero 2-col + 5 tabs (Overview · Evaluation · Documents ·
 * Interviews · Notes) + sidebar sticky + modal de video + sheet "Not for now".
 *
 * Datos:
 *   - Reales (BE): nombre, empresa, seniority, ubicación, skills, experiencia, videos,
 *     evaluaciones (cards reusan EvaluationCard), comments (TalentCommentsPanel), actions
 *     (TalentActionsPanel).
 *   - Placeholders gracefules (no BE aún, ver SW-140 follow-ups): rate, time-to-join,
 *     engagement, vetting score, strengths, companies chips, verified badge, educación,
 *     idiomas, documentos. Se renderizan con copy "Not available yet" o con un dash
 *     (no se inventan valores fake).
 *
 * Documentos e interviews placeholder se modelan con un shape local hasta que el BE
 * exponga endpoints; cuando lleguen, swappear la fuente por un hook (`useTalentDocuments`,
 * `useTalentInterviews`) sin cambiar la UI.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { tokens } from '../../theme/tokens';
import { ApiError } from '@sw/api-client';
import { useTenant } from '../../lib/tenant';
import { useTalentDetail } from './useTalentDetail';
import { useTalentExperiences } from './useTalentExperiences';
import { useTalentEducation } from './useTalentEducation';
import { useTalentCertifications } from './useTalentCertifications';
import { useTalentVideos } from './useTalentVideos';
import { useTalentEvaluations } from './useTalentEvaluations';
import { useShortlists } from './useShortlists';
import { VideoPlayer } from './VideoPlayer';
import { EvaluationCard } from './EvaluationCard';
import { TalentActionsPanel } from './TalentActionsPanel';
import { TalentCommentsPanel } from './TalentCommentsPanel';
import type { TalentDetail, TalentSkillDetail } from './talentDetailApi';
import type { WorkExperienceItem } from './talentExperiencesApi';
import type { EducationItem } from './talentEducationApi';
import type { CertificationItem } from './talentCertificationsApi';

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type TabKey = 'overview' | 'evaluation' | 'documents' | 'interviews' | 'notes';

export default function TalentDetailPage() {
  const { t, i18n } = useTranslation();
  const params = useParams<{ shortlistId: string; talentId: string }>();
  const shortlistPublicKey = params.shortlistId ?? '';
  const talentPublicKey = params.talentId ?? '';
  const isValidIds = GUID_RE.test(shortlistPublicKey) && GUID_RE.test(talentPublicKey);

  const query = useTalentDetail({
    shortlistPublicKey: isValidIds ? shortlistPublicKey : '00000000-0000-0000-0000-000000000000',
    talentPublicKey: isValidIds ? talentPublicKey : '00000000-0000-0000-0000-000000000000',
  });

  const isNotFound =
    !isValidIds || (query.error instanceof ApiError && query.error.status === 404);

  return (
    <Box
      data-testid="talent-detail-page"
      sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      <BreadcrumbBar
        shortlistPublicKey={isValidIds ? shortlistPublicKey : null}
        talentName={query.data?.fullName ?? null}
      />

      {isNotFound && <NotFoundState />}

      {!isNotFound && query.isPending && <LoadingSkeleton />}

      {!isNotFound && query.isSuccess && (
        <TalentBody
          talent={query.data}
          locale={i18n.language}
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
        />
      )}
    </Box>
  );
}

// ============================================================================
// BREADCRUMB
// ============================================================================

function BreadcrumbBar({
  shortlistPublicKey,
  talentName,
}: {
  shortlistPublicKey: string | null;
  talentName: string | null;
}) {
  const { t } = useTranslation();
  const shortlistsQuery = useShortlists();
  const items = shortlistsQuery.data?.items;
  const shortlistName =
    shortlistPublicKey && Array.isArray(items)
      ? items.find((s) => s.id === shortlistPublicKey)?.name
      : null;

  return (
    <Box
      component="nav"
      aria-label="Breadcrumb"
      data-testid="talent-detail-back"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        padding: '14px 0 18px',
        color: tokens.color.muted,
        fontSize: 13,
        borderBottom: `1px solid ${tokens.color.line}`,
        mb: '4px',
      }}
    >
      <Box
        component={Link}
        to="/opportunities"
        sx={{
          color: tokens.color.muted,
          textDecoration: 'none',
          padding: '4px 8px',
          borderRadius: '6px',
          '&:hover': { color: tokens.color.ink, background: tokens.color.surface2 },
        }}
      >
        {t('opportunities.talent.breadcrumb.opportunities')}
      </Box>
      <Box component="span" sx={{ color: tokens.color.muted2, fontSize: 11 }}>
        /
      </Box>
      {shortlistPublicKey ? (
        <Box
          component={Link}
          to={`/opportunities/${shortlistPublicKey}`}
          data-testid="talent-detail-back-shortlist"
          sx={{
            color: tokens.color.muted,
            textDecoration: 'none',
            padding: '4px 8px',
            borderRadius: '6px',
            '&:hover': { color: tokens.color.ink, background: tokens.color.surface2 },
          }}
        >
          {shortlistName ?? t('opportunities.detail.titleFallback')}
        </Box>
      ) : (
        <Box component="span" sx={{ color: tokens.color.muted }}>
          {t('opportunities.detail.titleFallback')}
        </Box>
      )}
      <Box component="span" sx={{ color: tokens.color.muted2, fontSize: 11 }}>
        /
      </Box>
      <Box component="span" sx={{ color: tokens.color.ink, fontWeight: 600, padding: '4px 8px' }}>
        {talentName ?? t('opportunities.talent.breadcrumb.loading')}
      </Box>
    </Box>
  );
}

// ============================================================================
// BODY (hero + tabs + panels + modals)
// ============================================================================

function TalentBody({
  talent,
  locale,
  shortlistPublicKey,
  talentPublicKey,
}: {
  talent: TalentDetail;
  locale: string;
  shortlistPublicKey: string;
  talentPublicKey: string;
}) {
  const { t } = useTranslation();
  const tenant = useTenant();
  const workspaceName = tenant.tenant?.name ?? t('tenant.fallbackName');
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [videoModal, setVideoModal] = useState<VideoModalState | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const commentsAnchorRef = useRef<HTMLDivElement | null>(null);

  const experiencesQuery = useTalentExperiences({ shortlistPublicKey, talentPublicKey });
  const videosQuery = useTalentVideos({ shortlistPublicKey, talentPublicKey });
  const evaluationsQuery = useTalentEvaluations({ shortlistPublicKey, talentPublicKey });

  const evalCount = evaluationsQuery.data?.items.length ?? 0;
  const interviewsCount = videosQuery.data?.items.length ?? 0;
  // Documents / Notes counts no se exponen via BE — los dejamos en 0 para no inventar.
  const documentsCount = 0;
  const notesCount = 0;

  function scrollToComments() {
    setActiveTab('overview');
    requestAnimationFrame(() => {
      commentsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  return (
    <Box data-testid="talent-detail-body">
      <Hero
        talent={talent}
        locale={locale}
        experiencesQuery={experiencesQuery}
        onWatchIntro={() => {
          const first = videosQuery.data?.items[0];
          if (first) {
            setVideoModal({
              shortlistPublicKey,
              talentPublicKey,
              videoPublicKey: first.id,
              title: first.title,
              durationSeconds: first.durationSeconds,
            });
          } else {
            setActiveTab('interviews');
          }
        }}
        onViewDocuments={() => setActiveTab('documents')}
        hasIntroVideo={interviewsCount > 0}
      />

      <Tabs
        active={activeTab}
        onChange={setActiveTab}
        counts={{
          evaluation: evalCount,
          documents: documentsCount,
          interviews: interviewsCount,
          notes: notesCount,
        }}
      />

      {activeTab === 'overview' && (
        <OverviewPanel
          talent={talent}
          locale={locale}
          workspaceName={workspaceName}
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          experiencesQuery={experiencesQuery}
          videosQuery={videosQuery}
          onPlayVideo={(v) => setVideoModal(v)}
          onOpenSheet={() => setSheetOpen(true)}
          onGoEvaluation={() => setActiveTab('evaluation')}
          onGoDocuments={() => setActiveTab('documents')}
          onGoInterviews={() => setActiveTab('interviews')}
          onLeaveComment={scrollToComments}
          commentsAnchorRef={commentsAnchorRef}
        />
      )}

      {activeTab === 'evaluation' && (
        <EvaluationPanel
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          talentSkills={talent.skills}
          onOpenSheet={() => setSheetOpen(true)}
        />
      )}

      {activeTab === 'documents' && (
        <DocumentsPanel onOpenSheet={() => setSheetOpen(true)} />
      )}

      {activeTab === 'interviews' && (
        <InterviewsPanel
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          videosQuery={videosQuery}
          onOpenSheet={() => setSheetOpen(true)}
        />
      )}

      {activeTab === 'notes' && (
        <NotesPanel
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          workspaceName={workspaceName}
          onOpenSheet={() => setSheetOpen(true)}
        />
      )}

      {videoModal && (
        <VideoModal state={videoModal} onClose={() => setVideoModal(null)} />
      )}

      {sheetOpen && (
        <DeclineSheet
          talentName={talent.fullName}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </Box>
  );
}

// ============================================================================
// HERO
// ============================================================================

function Hero({
  talent,
  locale,
  experiencesQuery,
  onWatchIntro,
  onViewDocuments,
  hasIntroVideo,
}: {
  talent: TalentDetail;
  locale: string;
  experiencesQuery: ReturnType<typeof useTalentExperiences>;
  onWatchIntro: () => void;
  onViewDocuments: () => void;
  hasIntroVideo: boolean;
}) {
  const { t } = useTranslation();
  const initials = computeInitials(talent.fullName);
  const { firstName, surname } = splitName(talent.fullName);
  const location = formatLocation(talent.city, talent.country);
  const availabilityLabel = formatAvailabilityShort(talent.availableFrom, locale, t);
  // SW-141 Phase 1: companies chips derivadas de WorkExperience (group by
  // CompanyName, take min/max year). No requiere BE nuevo. SW-141b code-review
  // H-1 fix: useMemo evita re-compute O(n log n) en cada render del padre
  // (tab switch, modal state, etc.).
  const companies = useMemo(
    () => deriveCompanies(experiencesQuery.data?.items ?? [], t),
    [experiencesQuery.data?.items, t],
  );
  // SW-141b code-review H-3 fix: capturar resultado de topStrengths en var local.
  const strengths = topStrengths(talent);

  return (
    <Box
      component="section"
      data-testid="talent-detail-header"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 360px' },
        gap: '28px',
        alignItems: 'stretch',
        paddingBottom: '28px',
        borderBottom: `1px solid ${tokens.color.line}`,
        mb: '28px',
      }}
    >
      {/* LEFT */}
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr auto', gap: '22px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontFamily: tokens.font.mono,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: 11,
            color: tokens.color.muted,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: tokens.color.pos,
              boxShadow: `0 0 0 4px ${tokens.color.posSoft}`,
            }}
          />
          {t('opportunities.talent.hero.eyebrow')}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '96px 1fr',
            gap: '22px',
            alignItems: 'start',
          }}
        >
          <Box
            data-testid="talent-detail-avatar"
            sx={{
              width: 96,
              height: 96,
              borderRadius: '24px',
              overflow: 'hidden',
              border: `1px solid ${tokens.color.line}`,
              position: 'relative',
              background: tokens.color.surface2,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                fontFamily: tokens.font.display,
                fontSize: 36,
                fontWeight: 600,
                letterSpacing: '-0.04em',
                color: tokens.color.ink,
                background: `linear-gradient(140deg, ${tokens.color.surface2}, ${tokens.color.bg})`,
              }}
            >
              {initials}
            </Box>
          </Box>
          <Box>
            <Typography
              component="h1"
              data-testid="talent-detail-name"
              sx={{
                fontFamily: tokens.font.display,
                fontWeight: 600,
                fontSize: 'clamp(40px, 5.5vw, 80px)',
                lineHeight: 0.95,
                letterSpacing: '-0.045em',
                margin: 0,
                color: tokens.color.ink,
              }}
            >
              {firstName}{' '}
              <Box
                component="span"
                sx={{ fontWeight: 400, color: tokens.color.ink2 }}
              >
                {surname}
              </Box>
            </Typography>
            <Typography
              data-testid="talent-detail-role"
              sx={{
                fontSize: 18,
                color: tokens.color.ink2,
                fontWeight: 500,
                letterSpacing: '-0.01em',
                margin: 0,
                mt: '18px',
              }}
            >
              {/* SW-141 Phase 1: si Candidate.Role existe (custom headline),
                  preferirlo sobre el composed "{seniority} at {company}". */}
              {talent.role ?? composeRole(talent.seniority, talent.currentCompany, t)}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box
            data-testid="talent-detail-hero-meta"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px 24px',
              color: tokens.color.muted,
              fontSize: 13,
              mb: '18px',
            }}
          >
            {location && (
              <Box component="span" data-testid="talent-detail-location" sx={metaItemSx}>
                <Box component="b" sx={{ color: tokens.color.ink, fontWeight: 600 }}>
                  {location}
                </Box>
              </Box>
            )}
            {talent.currentCompany && (
              <>
                <MetaSeparator />
                <Box component="span" data-testid="talent-detail-company" sx={metaItemSx}>
                  <Box component="b" sx={{ color: tokens.color.ink, fontWeight: 600 }}>
                    {talent.currentCompany}
                  </Box>
                </Box>
              </>
            )}
            {availabilityLabel && (
              <>
                <MetaSeparator />
                <Box
                  component="span"
                  data-testid="talent-detail-availability"
                  sx={{ ...metaItemSx, color: tokens.color.pos }}
                >
                  <Box component="b" sx={{ color: tokens.color.pos, fontWeight: 600 }}>
                    {availabilityLabel}
                  </Box>
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <PrimaryButton
              data-testid="hero-request-interview"
              onClick={() => {
                // El CTA del hero refleja el del actions panel — para mantenerlo
                // funcional sin duplicar mutación, scroll al panel de acciones.
                const el = document.querySelector('[data-testid="talent-actions-panel"]');
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            >
              {t('opportunities.talent.hero.requestInterview')}
            </PrimaryButton>
            <GhostButton
              data-testid="hero-watch-intro"
              onClick={onWatchIntro}
              disabled={!hasIntroVideo}
            >
              {hasIntroVideo
                ? t('opportunities.talent.hero.watchIntro')
                : t('opportunities.talent.hero.noIntro')}
            </GhostButton>
            <GhostButton data-testid="hero-view-documents" onClick={onViewDocuments}>
              {t('opportunities.talent.hero.viewDocuments')}
            </GhostButton>
          </Box>

          {/* SW-141 Phase 1: companies chips — Worked at + last 4 employers
              derivados de WorkExperience (group by CompanyName con year range). */}
          {companies.length > 0 && (
            <Box
              data-testid="hero-companies"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '8px',
                pt: '14px',
                borderTop: `1px dashed ${tokens.color.lineSoft}`,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: tokens.font.mono,
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: tokens.color.muted2,
                  mr: '4px',
                }}
              >
                {t('opportunities.talent.hero.workedAt')}
              </Box>
              {companies.map((c) => (
                <Box
                  key={c.name}
                  data-testid="hero-company-chip"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '5px 11px 5px 6px',
                    background: tokens.color.surface2,
                    border: `1px solid ${tokens.color.line}`,
                    borderRadius: tokens.size.radiusPill,
                    fontSize: 12,
                    fontWeight: 500,
                    color: tokens.color.ink2,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: tokens.color.accent,
                      color: tokens.color.accentInk,
                      display: 'grid',
                      placeItems: 'center',
                      fontFamily: tokens.font.mono,
                      fontSize: 9,
                      fontWeight: 600,
                    }}
                  >
                    {companyInitials(c.name)}
                  </Box>
                  {c.name}
                  <Box
                    component="span"
                    sx={{
                      fontFamily: tokens.font.mono,
                      fontSize: 10,
                      color: tokens.color.muted,
                    }}
                  >
                    {c.yearRange}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* RIGHT — At a glance */}
      <Box
        component="aside"
        data-testid="hero-at-a-glance"
        sx={{
          background: tokens.color.surface,
          border: `1px solid ${tokens.color.line}`,
          borderRadius: '20px',
          boxShadow: '0 1px 0 rgba(15,15,15,.04), 0 8px 24px -12px rgba(15,15,15,.10)',
          padding: '22px',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          gap: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: tokens.font.mono,
            textTransform: 'uppercase',
            fontSize: 10,
            letterSpacing: '0.1em',
            color: tokens.color.muted,
          }}
        >
          <span>{t('opportunities.talent.hero.atAGlance')}</span>
          {talent.isVerified && (
            <Box
              component="b"
              data-testid="hero-verified-badge"
              sx={{ color: tokens.color.pos, fontWeight: 600 }}
            >
              ✓ {t('opportunities.talent.hero.verified')}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1px',
            background: tokens.color.line,
            borderRadius: '12px',
            overflow: 'hidden',
            border: `1px solid ${tokens.color.line}`,
          }}
        >
          {/* SW-141 Phase 1: rate/vetting reales del BE (TalentDetailVm). Engagement
              sigue placeholder porque no hay BE — se resolverá en Fase 4. */}
          <QuickCell
            label={t('opportunities.talent.hero.rate')}
            value={talent.hourlyRate != null ? `$${talent.hourlyRate}` : '—'}
            valueSuffix={
              talent.hourlyRate != null ? t('opportunities.talent.hero.rateSuffix') : undefined
            }
          />
          <QuickCell
            label={t('opportunities.talent.hero.timeToJoin')}
            value={availabilityLabel ?? '—'}
            valueColor={availabilityLabel ? tokens.color.pos : undefined}
          />
          <QuickCell label={t('opportunities.talent.hero.engagement')} value="—" small />
          <QuickCell
            label={t('opportunities.talent.hero.vetting')}
            value={talent.vettingScore != null ? String(talent.vettingScore) : '—'}
            valueSuffix={
              talent.vettingScore != null ? t('opportunities.talent.hero.vettingSuffix') : undefined
            }
          />
        </Box>

        <Box sx={{ display: 'grid', gap: '6px' }}>
          {strengths.length === 0 ? (
            <Box
              sx={{
                padding: '9px 11px',
                background: tokens.color.surface2,
                borderRadius: '10px',
                fontSize: 13,
                color: tokens.color.muted,
              }}
            >
              {t('opportunities.talent.hero.strengthsEmpty')}
            </Box>
          ) : (
            strengths.map((s) => (
              <Box
                key={s}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 11px',
                  background: tokens.color.surface2,
                  borderRadius: '10px',
                  fontSize: 13,
                  color: tokens.color.ink,
                  fontWeight: 500,
                }}
              >
                <CheckIcon size={14} color={tokens.color.accent} />
                {s}
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}

function topStrengths(talent: TalentDetail): string[] {
  // Heurística: las 3 primeras skills del BE. No hay endpoint dedicado de "strengths"
  // aún; cuando exista, reemplazar este derive por la fuente real.
  return talent.skills.slice(0, 3).map((s) => s.name);
}

/**
 * SW-141 Phase 1: deriva las companies chips del hero desde WorkExperience.
 * Group by CompanyName + year range (min start year, max end year), ordenado
 * por start desc (más reciente primero). Cap a 4 chips para evitar wrap horrible.
 *
 * SW-141b code-review H-2 fix: "Now" pasa por i18n via `t` parameter (era
 * literal en inglés en chip visible). Caller pasa `t` desde useTranslation.
 */
function deriveCompanies(
  items: WorkExperienceItem[],
  t: ReturnType<typeof useTranslation>['t'],
): Array<{ name: string; yearRange: string }> {
  if (items.length === 0) return [];
  const byCompany = new Map<string, { minStart: Date; maxEnd: Date | null; isCurrent: boolean }>();
  for (const exp of items) {
    if (!exp.companyName) continue;
    const start = new Date(exp.startDate);
    const end = exp.endDate ? new Date(exp.endDate) : null;
    const prev = byCompany.get(exp.companyName);
    if (!prev) {
      byCompany.set(exp.companyName, { minStart: start, maxEnd: end, isCurrent: end === null });
    } else {
      if (start < prev.minStart) prev.minStart = start;
      if (end === null) {
        prev.isCurrent = true;
      } else if (prev.maxEnd && end > prev.maxEnd) {
        prev.maxEnd = end;
      }
    }
  }
  const presentLabel = t('opportunities.talent.hero.presentYear');
  return Array.from(byCompany.entries())
    .sort(([, a], [, b]) => b.minStart.getTime() - a.minStart.getTime())
    .slice(0, 4)
    .map(([name, range]) => ({
      name,
      yearRange: range.isCurrent
        ? `${range.minStart.getFullYear()}–${presentLabel}`
        : `${range.minStart.getFullYear()}–${(range.maxEnd ?? range.minStart).getFullYear().toString().slice(-2)}`,
    }));
}

/** Iniciales 2-char de la empresa para el chip avatar circular. */
function companyInitials(name: string): string {
  const words = name.replace(/[^a-zA-Z0-9 ]/g, '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

const metaItemSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
};

function MetaSeparator() {
  return (
    <Box
      component="span"
      sx={{
        width: 1,
        height: 12,
        background: tokens.color.line,
        display: 'inline-block',
        alignSelf: 'center',
      }}
    />
  );
}

function QuickCell({
  label,
  value,
  small,
  valueColor,
  valueSuffix,
}: {
  label: string;
  value: string;
  small?: boolean;
  valueColor?: string;
  /** Sufijo opcional (ej. "/hr", "/100") en muted secondary color. SW-141 Phase 1. */
  valueSuffix?: string;
}) {
  return (
    <Box
      sx={{
        padding: '14px',
        background: tokens.color.surface,
        display: 'grid',
        gap: '4px',
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: tokens.color.muted,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: small ? 14 : 17,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: valueColor ?? tokens.color.ink,
        }}
      >
        {value}
        {valueSuffix && (
          <Box
            component="span"
            sx={{ fontSize: 12, color: tokens.color.muted, fontWeight: 500, ml: '2px' }}
          >
            {valueSuffix}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// TABS
// ============================================================================

function Tabs({
  active,
  onChange,
  counts,
}: {
  active: TabKey;
  onChange: (k: TabKey) => void;
  counts: { evaluation: number; documents: number; interviews: number; notes: number };
}) {
  const { t } = useTranslation();
  const items: Array<{ key: TabKey; label: string; count?: number }> = [
    { key: 'overview', label: t('opportunities.talent.tabs.overview') },
    { key: 'evaluation', label: t('opportunities.talent.tabs.evaluation'), count: counts.evaluation },
    { key: 'documents', label: t('opportunities.talent.tabs.documents'), count: counts.documents },
    { key: 'interviews', label: t('opportunities.talent.tabs.interviews'), count: counts.interviews },
    { key: 'notes', label: t('opportunities.talent.tabs.notes'), count: counts.notes },
  ];

  return (
    <Box
      role="tablist"
      data-testid="talent-detail-tabs"
      sx={{
        display: 'flex',
        gap: 0,
        borderBottom: `1px solid ${tokens.color.line}`,
        mb: '28px',
        overflowX: 'auto',
      }}
    >
      {items.map((it, idx) => {
        const isActive = active === it.key;
        // M-2 fix: WAI-ARIA Tabs pattern — keyboard nav con Arrow keys + tabIndex
        // dinámico (solo el activo es tab-stop) + aria-controls + id para que el
        // tabpanel correspondiente referencie via aria-labelledby.
        return (
          <Box
            key={it.key}
            component="button"
            role="tab"
            id={`tab-${it.key}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${it.key}`}
            tabIndex={isActive ? 0 : -1}
            data-testid={`tab-${it.key}`}
            onClick={() => onChange(it.key)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault();
                const next = items[(idx + 1) % items.length]!;
                onChange(next.key);
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                const prev = items[(idx - 1 + items.length) % items.length]!;
                onChange(prev.key);
              } else if (e.key === 'Home') {
                e.preventDefault();
                onChange(items[0]!.key);
              } else if (e.key === 'End') {
                e.preventDefault();
                onChange(items[items.length - 1]!.key);
              }
            }}
            sx={{
              appearance: 'none',
              background: 'transparent',
              border: 0,
              padding: '12px 18px 14px',
              mb: '-1px',
              borderBottom: `2px solid ${isActive ? tokens.color.accent : 'transparent'}`,
              color: isActive ? tokens.color.ink : tokens.color.muted,
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: isActive ? 600 : 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
              '&:hover': { color: tokens.color.ink },
            }}
          >
            {it.label}
            {it.count !== undefined && it.count > 0 && (
              <Box
                component="span"
                sx={{
                  fontFamily: tokens.font.mono,
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: tokens.size.radiusPill,
                  background: isActive ? tokens.color.accent : tokens.color.surface2,
                  color: isActive ? tokens.color.accentInk : tokens.color.muted,
                }}
              >
                {it.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ============================================================================
// OVERVIEW PANEL
// ============================================================================

function OverviewPanel({
  talent,
  locale,
  workspaceName,
  shortlistPublicKey,
  talentPublicKey,
  experiencesQuery,
  videosQuery,
  onPlayVideo,
  onOpenSheet,
  onGoEvaluation,
  onGoDocuments,
  onGoInterviews,
  onLeaveComment,
  commentsAnchorRef,
}: {
  talent: TalentDetail;
  locale: string;
  workspaceName: string;
  shortlistPublicKey: string;
  talentPublicKey: string;
  experiencesQuery: ReturnType<typeof useTalentExperiences>;
  videosQuery: ReturnType<typeof useTalentVideos>;
  onPlayVideo: (s: VideoModalState) => void;
  onOpenSheet: () => void;
  onGoEvaluation: () => void;
  onGoDocuments: () => void;
  onGoInterviews: () => void;
  onLeaveComment: () => void;
  commentsAnchorRef: React.MutableRefObject<HTMLDivElement | null>;
}) {
  return (
    <Box
      role="tabpanel"
      id="tabpanel-overview"
      aria-labelledby="tab-overview"
      data-testid="panel-overview"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
        gap: '24px',
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'grid', gap: '16px', minWidth: 0 }}>
        <SummarySection talent={talent} onGoEvaluation={onGoEvaluation} />
        <MetricsStrip talent={talent} experiencesQuery={experiencesQuery} />
        <ExperienceSection query={experiencesQuery} workspaceName={workspaceName} locale={locale} />
        <EducationSection
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          locale={locale}
        />
        <SkillsSection skills={talent.skills} />
        <Box ref={commentsAnchorRef}>
          <TalentCommentsPanel
            shortlistPublicKey={shortlistPublicKey}
            talentPublicKey={talentPublicKey}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: '16px', position: { md: 'sticky' }, top: 20 }}>
        <ActionsCard
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          onOpenSheet={onOpenSheet}
          onLeaveComment={onLeaveComment}
        />
        <DocumentsSidePreview onViewAll={onGoDocuments} />
        <VideosSidePreview
          videosQuery={videosQuery}
          onPlay={(v) =>
            onPlayVideo({
              shortlistPublicKey,
              talentPublicKey,
              videoPublicKey: v.id,
              title: v.title,
              durationSeconds: v.durationSeconds,
            })
          }
          onViewAll={onGoInterviews}
        />
        <KeyFactsCard talent={talent} locale={locale} />
        <LanguagesCard />
      </Box>
    </Box>
  );
}

function SummarySection({
  talent,
  onGoEvaluation,
}: {
  talent: TalentDetail;
  onGoEvaluation: () => void;
}) {
  const { t } = useTranslation();
  const { firstName } = splitName(talent.fullName);
  const fits = talent.skills.slice(0, 4).map((s) => s.name);

  return (
    <SectionCard
      eyebrow={t('opportunities.talent.summary.eyebrow')}
      title={t('opportunities.talent.summary.title', { name: firstName })}
      action={
        <SectionLink onClick={onGoEvaluation} testId="summary-view-evaluation">
          {t('opportunities.talent.summary.viewEvaluation')} →
        </SectionLink>
      }
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1.1fr 0.9fr' },
          gap: '28px',
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            fontSize: 16,
            lineHeight: 1.7,
            color: tokens.color.ink2,
            maxWidth: '64ch',
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: 18,
              fontWeight: 500,
              color: tokens.color.ink,
              mb: '12px',
              letterSpacing: '-0.01em',
            }}
          >
            {composeLede(talent, t)}
          </Box>
          {/* SW-141 Phase 1: Bio real del candidate cuando esté expuesta (BE applies
              ShowBio visibility toggle); fallback al copy genérico si null. */}
          {talent.bio ?? t('opportunities.talent.summary.body')}
        </Box>
        {fits.length > 0 ? (
          <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '10px' }}>
            {fits.map((f) => (
              <Box
                key={f}
                component="li"
                data-testid="summary-fit-item"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '18px 1fr',
                  gap: '10px',
                  fontSize: 14,
                  color: tokens.color.ink2,
                  alignItems: 'start',
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: tokens.color.accent,
                    color: tokens.color.accentInk,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    mt: '2px',
                  }}
                >
                  <CheckIcon size={10} />
                </Box>
                {f}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ color: tokens.color.muted, fontSize: 13 }}>
            {t('opportunities.talent.summary.fitsEmpty')}
          </Typography>
        )}
      </Box>
    </SectionCard>
  );
}

function composeLede(talent: TalentDetail, t: ReturnType<typeof useTranslation>['t']): string {
  const role = talent.seniority || t('opportunities.talent.summary.fallbackRole');
  const company = talent.currentCompany;
  if (company) {
    return t('opportunities.talent.summary.ledeWithCompany', { role, company });
  }
  return t('opportunities.talent.summary.ledeNoCompany', { role });
}

function composeRole(
  seniority: string | null,
  company: string | null,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const parts: string[] = [];
  if (seniority) parts.push(seniority);
  if (company) parts.push(company);
  return parts.length > 0 ? parts.join(' · ') : t('opportunities.talent.hero.roleFallback');
}

function MetricsStrip({
  talent,
  experiencesQuery,
}: {
  talent: TalentDetail;
  experiencesQuery: ReturnType<typeof useTalentExperiences>;
}) {
  const { t } = useTranslation();
  const yearsOfExp = useMemo(() => {
    const items = experiencesQuery.data?.items ?? [];
    if (items.length === 0) return null;
    const earliest = items.reduce<Date | null>((acc, e) => {
      const d = new Date(e.startDate);
      if (Number.isNaN(d.getTime())) return acc;
      if (!acc || d < acc) return d;
      return acc;
    }, null);
    if (!earliest) return null;
    const diffMs = Date.now() - earliest.getTime();
    return Math.max(1, Math.floor(diffMs / (365.25 * 24 * 60 * 60 * 1000)));
  }, [experiencesQuery.data]);

  const projectsShipped = experiencesQuery.data?.items.length ?? null;

  return (
    <Box
      data-testid="metrics-strip"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: '1px',
        background: tokens.color.line,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '14px',
        overflow: 'hidden',
      }}
    >
      <Metric
        label={t('opportunities.talent.metrics.yearsExp')}
        value={yearsOfExp != null ? `${yearsOfExp}+` : '—'}
        sub={t('opportunities.talent.metrics.yearsExpSub')}
      />
      <Metric
        label={t('opportunities.talent.metrics.vetting')}
        value="—"
        sub={t('opportunities.talent.metrics.vettingSub')}
      />
      <Metric
        label={t('opportunities.talent.metrics.projects')}
        value={projectsShipped != null ? `${projectsShipped}` : '—'}
        sub={
          talent.skills[0]?.name
            ? talent.skills.slice(0, 3).map((s) => s.name).join(' · ')
            : t('opportunities.talent.metrics.projectsSub')
        }
      />
    </Box>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Box
      sx={{
        padding: '18px',
        background: tokens.color.surface,
        display: 'grid',
        gap: '6px',
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.mono,
          textTransform: 'uppercase',
          fontSize: 10,
          letterSpacing: '0.1em',
          color: tokens.color.muted,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.display,
          fontSize: 32,
          fontWeight: 600,
          letterSpacing: '-0.04em',
          color: tokens.color.ink,
          lineHeight: 1,
        }}
      >
        {value}
      </Box>
      <Box component="span" sx={{ fontSize: 12, color: tokens.color.muted }}>
        {sub}
      </Box>
    </Box>
  );
}

// ============================================================================
// EXPERIENCE (with expand + older toggle)
// ============================================================================

function ExperienceSection({
  query,
  workspaceName,
  locale,
}: {
  query: ReturnType<typeof useTalentExperiences>;
  workspaceName: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const [showOlder, setShowOlder] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleExtra(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const items = query.data?.items ?? [];
  const sorted = [...items].sort((a, b) => {
    const aT = new Date(a.startDate).getTime();
    const bT = new Date(b.startDate).getTime();
    return bT - aT;
  });
  const recent = sorted.slice(0, 3);
  const older = sorted.slice(3);

  return (
    <SectionCard
      eyebrow={t('opportunities.talent.experience.eyebrow')}
      title={t('opportunities.talent.sections.experience')}
      note={t('opportunities.talent.experience.note')}
    >
      {query.isPending && (
        <Box
          data-testid="talent-experience-loading"
          sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={64} />
          ))}
        </Box>
      )}

      {(query.isError || (query.isSuccess && items.length === 0)) && (
        <Typography
          data-testid="talent-experience-empty"
          sx={{ color: tokens.color.muted, fontSize: 13 }}
        >
          {t('opportunities.talent.sections.experienceEmpty')}
        </Typography>
      )}

      {query.isSuccess && items.length > 0 && (
        <>
          <Box sx={{ display: 'grid', gap: 0 }}>
            {recent.map((exp, idx) => (
              <ExperienceRow
                key={exp.id}
                experience={exp}
                workspaceName={workspaceName}
                locale={locale}
                isFirst={idx === 0}
                expanded={expanded.has(exp.id)}
                onToggle={() => toggleExtra(exp.id)}
              />
            ))}
            {showOlder &&
              older.map((exp) => (
                <ExperienceRow
                  key={exp.id}
                  experience={exp}
                  workspaceName={workspaceName}
                  locale={locale}
                  isFirst={false}
                  expanded={expanded.has(exp.id)}
                  onToggle={() => toggleExtra(exp.id)}
                />
              ))}
          </Box>

          {older.length > 0 && (
            <Box
              component="button"
              type="button"
              data-testid="older-toggle"
              onClick={() => setShowOlder((v) => !v)}
              sx={{
                width: '100%',
                mt: '18px',
                padding: '14px',
                background: 'transparent',
                border: `1px dashed ${tokens.color.line}`,
                borderRadius: '12px',
                color: tokens.color.muted,
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                '&:hover': {
                  borderColor: tokens.color.ink,
                  color: tokens.color.ink,
                  background: tokens.color.surface2,
                },
              }}
            >
              {showOlder
                ? t('opportunities.talent.experience.hideOlder')
                : t('opportunities.talent.experience.showOlder', { count: older.length })}
              <span>{showOlder ? '−' : '+'}</span>
            </Box>
          )}
        </>
      )}
    </SectionCard>
  );
}

function ExperienceRow({
  experience,
  workspaceName,
  locale,
  isFirst,
  expanded,
  onToggle,
}: {
  experience: WorkExperienceItem;
  workspaceName: string;
  locale: string;
  isFirst: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const isInternal = experience.provenance === 1;
  const range = formatDateRange(experience.startDate, experience.endDate, locale, t);
  const duration = formatDuration(experience.startDate, experience.endDate, t);
  // SW-149 code-rev Mi-1: bullets extraídos antes del return (era IIFE inline).
  const bullets = parseHighlights(experience.highlights);

  return (
    <Box
      component="article"
      data-testid="talent-experience-row"
      data-provenance={isInternal ? 'workspace-internal' : 'independent'}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '110px 1fr' },
        gap: '22px',
        padding: isFirst ? '0 0 22px' : '22px 0',
        borderTop: isFirst ? 0 : `1px solid ${tokens.color.line}`,
      }}
    >
      <Box sx={{ display: 'grid', gap: '4px', alignContent: 'start' }}>
        <Box
          component="span"
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: 12,
            color: tokens.color.ink,
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          {range}
        </Box>
        <Box
          component="span"
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: 11,
            color: tokens.color.muted,
          }}
        >
          {duration}
        </Box>
        {isInternal && (
          <Box
            component="span"
            data-testid="talent-experience-verified-badge"
            sx={{
              display: 'inline-flex',
              padding: '3px 8px',
              background: tokens.color.posSoft,
              color: tokens.color.pos,
              borderRadius: tokens.size.radiusPill,
              fontSize: 11,
              fontWeight: 600,
              width: 'max-content',
              mt: '4px',
              letterSpacing: '0.02em',
            }}
          >
            ✓ {t('opportunities.talent.experience.verifiedBy', { workspace: workspaceName })}
          </Box>
        )}
        {!isInternal && (
          <Box
            component="span"
            data-testid="talent-experience-selfreported-badge"
            sx={{
              display: 'inline-flex',
              padding: '3px 8px',
              background: tokens.color.surface2,
              color: tokens.color.ink2,
              borderRadius: tokens.size.radiusPill,
              fontSize: 11,
              fontWeight: 500,
              width: 'max-content',
              mt: '4px',
            }}
          >
            {t('opportunities.talent.experience.selfReported')}
          </Box>
        )}
      </Box>
      <Box>
        <Typography
          sx={{
            margin: 0,
            mb: '4px',
            fontFamily: tokens.font.display,
            fontSize: 17,
            fontWeight: 600,
            letterSpacing: '-0.015em',
            color: tokens.color.ink,
          }}
        >
          {experience.role}
        </Typography>
        <Box
          sx={{
            color: tokens.color.muted,
            fontSize: 13,
            mb: '12px',
          }}
        >
          <Box component="span" sx={{ color: tokens.color.ink2, fontWeight: 500 }}>
            {experience.companyName}
          </Box>
          {isInternal && experience.workspaceClientIndustry && (
            <Box
              component="span"
              data-testid="talent-experience-industry"
              sx={{ ml: '8px', fontSize: 12, color: tokens.color.muted }}
            >
              · {t('opportunities.talent.experience.industry', {
                industry: experience.workspaceClientIndustry,
              })}
            </Box>
          )}
        </Box>
        {experience.description && (
          <Typography
            sx={{
              margin: 0,
              color: tokens.color.ink2,
              fontSize: 14.5,
              lineHeight: 1.65,
              maxWidth: '60ch',
            }}
          >
            {experience.description}
          </Typography>
        )}
        {/* SW-149: highlights = newline-separated bullets detrás del toggle
            "See more / See less". El description queda siempre visible como
            summary narrativo del handoff §02-Experience. */}
        {bullets.length > 0 && (
          <>
            {/* SW-149 code-rev M-1: el <ul> SIEMPRE en el DOM con `hidden`
                cuando collapsed — `aria-controls` requiere que el elemento
                referenciado exista (sino screen readers no anuncian estado
                "collapsed"). Patrón consistente con el tabpanel arriba. */}
            <Box
              component="ul"
              id={`exp-highlights-${experience.id}`}
              data-testid={`exp-highlights-${experience.id}`}
              hidden={!expanded}
              sx={{
                margin: '12px 0 0',
                paddingLeft: '18px',
                color: tokens.color.ink2,
                fontSize: 13.5,
                lineHeight: 1.7,
                maxWidth: '60ch',
              }}
            >
              {bullets.map((bullet, idx) => (
                <Box component="li" key={`${experience.id}-${idx}`} sx={{ mb: '4px' }}>
                  {bullet}
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: '14px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Box
                component="button"
                type="button"
                data-testid={`exp-toggle-${experience.id}`}
                aria-expanded={expanded}
                aria-controls={`exp-highlights-${experience.id}`}
                onClick={onToggle}
                sx={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  color: tokens.color.accent,
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {expanded
                  ? t('opportunities.talent.experience.seeLess')
                  : t('opportunities.talent.experience.seeMore')}
                <span>{expanded ? '−' : '+'}</span>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

/**
 * SW-149: split del Highlights field BE (newline-separated string) en un array
 * de bullets. Filtra líneas vacías y trim de cada bullet. Caller decide cuándo
 * renderizar la lista (oculta detrás del toggle "See more").
 */
function parseHighlights(highlights: string | null): string[] {
  if (!highlights) return [];
  return highlights
    .split('\n')
    .map((b) => b.trim())
    .filter((b) => b.length > 0);
}

// ============================================================================
// EDUCATION + CERTIFICATIONS (SW-149b)
// ============================================================================

function EducationSection({
  shortlistPublicKey,
  talentPublicKey,
  locale,
}: {
  shortlistPublicKey: string;
  talentPublicKey: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const educationQuery = useTalentEducation({ shortlistPublicKey, talentPublicKey });
  const certificationsQuery = useTalentCertifications({ shortlistPublicKey, talentPublicKey });

  const educationItems = educationQuery.data?.items ?? [];
  const certificationItems = certificationsQuery.data?.items ?? [];

  const isLoading = educationQuery.isPending || certificationsQuery.isPending;
  const isEmpty =
    !isLoading &&
    educationItems.length === 0 &&
    certificationItems.length === 0;

  // Education sorted DESC por startDate (más reciente primero) — el BE ya hace
  // el order, mantenemos el sort defensivo para que el FE no dependa de eso.
  const sortedEducation = [...educationItems].sort((a, b) => {
    const aT = new Date(a.startDate).getTime();
    const bT = new Date(b.startDate).getTime();
    return bT - aT;
  });

  return (
    <SectionCard
      eyebrow={t('opportunities.talent.education.eyebrow')}
      title={t('opportunities.talent.education.title')}
      note={t('opportunities.talent.education.sub')}
    >
      {isLoading && (
        <Box
          data-testid="talent-education-loading"
          sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={56} />
          ))}
        </Box>
      )}

      {isEmpty && (
        <EmptySectionBlock
          text={t('opportunities.talent.education.empty')}
          testId="talent-education-empty"
        />
      )}

      {!isLoading && !isEmpty && (
        <Box sx={{ display: 'grid', gap: '20px' }}>
          {sortedEducation.length > 0 && (
            <Box
              data-testid="talent-education-list"
              sx={{ display: 'grid', gap: 0 }}
            >
              {sortedEducation.map((edu, idx) => (
                <EducationRow
                  key={edu.id}
                  education={edu}
                  locale={locale}
                  isFirst={idx === 0}
                />
              ))}
            </Box>
          )}

          {certificationItems.length > 0 && (
            <CertificationsSubsection items={certificationItems} locale={locale} />
          )}
        </Box>
      )}
    </SectionCard>
  );
}

function EducationRow({
  education,
  locale,
  isFirst,
}: {
  education: EducationItem;
  locale: string;
  isFirst: boolean;
}) {
  const { t } = useTranslation();
  const range = formatDateRange(education.startDate, education.endDate, locale, t);
  const isOngoing = !education.endDate;

  return (
    <Box
      component="article"
      data-testid="talent-education-row"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '110px 1fr' },
        gap: '22px',
        padding: isFirst ? '0 0 18px' : '18px 0',
        borderTop: isFirst ? 0 : `1px solid ${tokens.color.line}`,
      }}
    >
      <Box sx={{ display: 'grid', gap: '4px', alignContent: 'start' }}>
        <Box
          component="span"
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: 12,
            color: tokens.color.ink,
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          {range}
        </Box>
        {isOngoing && (
          <Box
            component="span"
            data-testid="talent-education-ongoing"
            sx={{
              display: 'inline-flex',
              padding: '3px 8px',
              background: tokens.color.surface2,
              color: tokens.color.ink2,
              borderRadius: tokens.size.radiusPill,
              fontSize: 11,
              fontWeight: 500,
              width: 'max-content',
              mt: '4px',
            }}
          >
            {t('opportunities.talent.education.ongoing')}
          </Box>
        )}
      </Box>
      <Box>
        <Typography
          sx={{
            margin: 0,
            mb: '2px',
            fontFamily: tokens.font.display,
            fontSize: 16,
            fontWeight: 600,
            color: tokens.color.ink,
            letterSpacing: '-0.01em',
          }}
        >
          {education.degree}
        </Typography>
        <Typography
          sx={{
            margin: 0,
            mb: education.field || education.description ? '6px' : 0,
            fontSize: 13,
            color: tokens.color.ink2,
          }}
        >
          {education.institution}
        </Typography>
        {education.field && (
          <Typography
            sx={{
              margin: 0,
              mb: education.description ? '6px' : 0,
              fontSize: 12,
              color: tokens.color.muted,
            }}
          >
            {education.field}
          </Typography>
        )}
        {education.description && (
          <Typography
            sx={{
              margin: 0,
              fontSize: 13,
              color: tokens.color.ink2,
              lineHeight: 1.55,
            }}
          >
            {education.description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function CertificationsSubsection({
  items,
  locale,
}: {
  items: CertificationItem[];
  locale: string;
}) {
  const { t } = useTranslation();

  // Order DESC by dateObtained (cert más reciente primero); items sin date al final.
  const sorted = [...items].sort((a, b) => {
    const aT = a.dateObtained ? new Date(a.dateObtained).getTime() : -Infinity;
    const bT = b.dateObtained ? new Date(b.dateObtained).getTime() : -Infinity;
    return bT - aT;
  });

  return (
    <Box
      data-testid="talent-certifications-section"
      sx={{
        paddingTop: '18px',
        borderTop: `1px solid ${tokens.color.line}`,
      }}
    >
      <Box
        sx={{
          fontFamily: tokens.font.mono,
          textTransform: 'uppercase',
          fontSize: 10,
          letterSpacing: '0.1em',
          color: tokens.color.muted,
          mb: '12px',
        }}
      >
        {t('opportunities.talent.education.certificationsTitle')}
      </Box>
      <Box sx={{ display: 'grid', gap: '12px' }}>
        {sorted.map((cert) => (
          <CertificationRow key={cert.id} certification={cert} locale={locale} />
        ))}
      </Box>
    </Box>
  );
}

function CertificationRow({
  certification,
  locale,
}: {
  certification: CertificationItem;
  locale: string;
}) {
  const { t } = useTranslation();
  const dateObtained = certification.dateObtained
    ? formatMonthYear(new Date(certification.dateObtained), locale)
    : null;
  const expiresText = certification.expirationDate
    ? t('opportunities.talent.education.expires', {
        date: formatMonthYear(new Date(certification.expirationDate), locale),
      })
    : null;

  return (
    <Box
      data-testid="talent-certification-row"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
        gap: '12px',
        alignItems: 'center',
        padding: '10px 14px',
        background: tokens.color.surface2,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '10px',
      }}
    >
      <Box>
        <Typography
          sx={{
            margin: 0,
            fontSize: 13,
            fontWeight: 600,
            color: tokens.color.ink,
          }}
        >
          {certification.name}
        </Typography>
        {certification.issuer && (
          <Typography
            sx={{
              margin: '2px 0 0',
              fontSize: 12,
              color: tokens.color.muted,
            }}
          >
            {certification.issuer}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          gap: '2px',
          fontFamily: tokens.font.mono,
          fontSize: 11,
        }}
      >
        {dateObtained && (
          <Box
            component="span"
            data-testid="talent-certification-date"
            sx={{ color: tokens.color.ink2, fontWeight: 600 }}
          >
            {dateObtained}
          </Box>
        )}
        {expiresText && (
          <Box
            component="span"
            data-testid="talent-certification-expires"
            sx={{ color: tokens.color.muted }}
          >
            {expiresText}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// SKILLS (3 cols if many, fallback to flex chips)
// ============================================================================

function SkillsSection({ skills }: { skills: TalentSkillDetail[] }) {
  const { t } = useTranslation();
  if (skills.length === 0) {
    return (
      <SectionCard
        eyebrow={t('opportunities.talent.skills.eyebrow')}
        title={t('opportunities.talent.sections.skills')}
      >
        <EmptySectionBlock
          text={t('opportunities.talent.sections.skillsEmpty')}
          testId="talent-skills-empty"
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      eyebrow={t('opportunities.talent.skills.eyebrow')}
      title={t('opportunities.talent.sections.skills')}
    >
      <Box
        data-testid="talent-detail-skills"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        {skills.map((s) => (
          <SkillBadge key={s.name} skill={s} />
        ))}
      </Box>
    </SectionCard>
  );
}

function SkillBadge({ skill }: { skill: TalentSkillDetail }) {
  const { t } = useTranslation();
  const years = skill.yearsOfExperience ?? 0;
  const yearsText =
    years > 0 ? ` · ${t('opportunities.talent.skillCard.years', { count: Math.round(years) })}` : '';
  return (
    <Box
      data-testid="talent-skill-badge"
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '8px 14px',
        borderRadius: '10px',
        border: `1px solid ${tokens.color.line}`,
        background: tokens.color.surface2,
      }}
    >
      <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: tokens.color.ink2 }}>
        {skill.name}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
        }}
      >
        {skill.level ?? ''}
        {yearsText}
      </Box>
    </Box>
  );
}

// ============================================================================
// EVALUATION PANEL
// ============================================================================

function EvaluationPanel({
  shortlistPublicKey,
  talentPublicKey,
  talentSkills,
  onOpenSheet,
}: {
  shortlistPublicKey: string;
  talentPublicKey: string;
  talentSkills: TalentSkillDetail[];
  onOpenSheet: () => void;
}) {
  const { t } = useTranslation();
  const query = useTalentEvaluations({ shortlistPublicKey, talentPublicKey });

  return (
    <Box
      role="tabpanel"
      id="tabpanel-evaluation"
      aria-labelledby="tab-evaluation"
      data-testid="panel-evaluation"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
        gap: '24px',
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'grid', gap: '16px', minWidth: 0 }}>
        <SectionCard
          eyebrow={t('opportunities.talent.evaluation.eyebrow')}
          title={t('opportunities.talent.evaluation.title')}
          note={t('opportunities.talent.evaluation.note')}
        >
          {query.isPending && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Skeleton variant="rounded" height={72} />
              <Skeleton variant="rounded" height={72} />
            </Box>
          )}
          {query.isSuccess && query.data.items.length === 0 && (
            <EmptySectionBlock
              text={t('opportunities.talent.evaluation.empty')}
              testId="talent-evaluations-empty"
            />
          )}
          {query.isSuccess && query.data.items.length > 0 && (
            <Box
              data-testid="talent-detail-evaluations"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: '12px',
              }}
            >
              {query.data.items.map((e) => (
                <EvaluationCard
                  key={e.id}
                  evaluation={e}
                  shortlistPublicKey={shortlistPublicKey}
                  talentPublicKey={talentPublicKey}
                />
              ))}
            </Box>
          )}
        </SectionCard>

        <SectionCard
          eyebrow={t('opportunities.talent.skills.eyebrow')}
          title={t('opportunities.talent.sections.skills')}
        >
          {talentSkills.length === 0 ? (
            <EmptySectionBlock
              text={t('opportunities.talent.sections.skillsEmpty')}
              testId="talent-skills-empty-eval"
            />
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {talentSkills.map((s) => (
                <SkillBadge key={s.name} skill={s} />
              ))}
            </Box>
          )}
        </SectionCard>
      </Box>

      <Box sx={{ display: 'grid', gap: '16px', position: { md: 'sticky' }, top: 20 }}>
        <ActionsCard
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          onOpenSheet={onOpenSheet}
        />
        <SectionCard eyebrow={t('opportunities.talent.evaluation.topScoresEyebrow')} title="">
          <EmptySectionBlock
            text={t('opportunities.talent.evaluation.topScoresEmpty')}
            testId="evaluation-top-scores-empty"
          />
        </SectionCard>
      </Box>
    </Box>
  );
}

// ============================================================================
// DOCUMENTS PANEL (placeholder — BE pendiente)
// ============================================================================

function DocumentsPanel({ onOpenSheet }: { onOpenSheet: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      role="tabpanel"
      id="tabpanel-documents"
      aria-labelledby="tab-documents"
      data-testid="panel-documents"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
        gap: '24px',
        alignItems: 'start',
      }}
    >
      <SectionCard
        eyebrow={t('opportunities.talent.documents.eyebrow')}
        title={t('opportunities.talent.documents.title')}
        note={t('opportunities.talent.documents.note')}
      >
        <EmptySectionBlock
          text={t('opportunities.talent.documents.empty')}
          testId="talent-documents-empty"
        />
      </SectionCard>
      <Box sx={{ display: 'grid', gap: '16px', position: { md: 'sticky' }, top: 20 }}>
        <Box />
        <Box
          sx={{
            padding: '20px',
            background: tokens.color.surface,
            border: `1px solid ${tokens.color.line}`,
            borderRadius: '20px',
            display: 'grid',
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              margin: 0,
              fontFamily: tokens.font.display,
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            {t('opportunities.talent.actions.decideTitle')}
          </Typography>
          <SidebarPrimaryAction onClick={onOpenSheet} />
        </Box>
      </Box>
    </Box>
  );
}

function SidebarPrimaryAction({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 14px',
        borderRadius: '10px',
        background: tokens.color.surface,
        color: tokens.color.ink,
        border: `1px solid ${tokens.color.line}`,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        '&:hover': { borderColor: tokens.color.ink },
      }}
    >
      {t('opportunities.talent.actions.notForNow')}
    </Box>
  );
}

// ============================================================================
// INTERVIEWS PANEL (videos full-list using VideoPlayer)
// ============================================================================

function InterviewsPanel({
  shortlistPublicKey,
  talentPublicKey,
  videosQuery,
  onOpenSheet,
}: {
  shortlistPublicKey: string;
  talentPublicKey: string;
  videosQuery: ReturnType<typeof useTalentVideos>;
  onOpenSheet: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      role="tabpanel"
      id="tabpanel-interviews"
      aria-labelledby="tab-interviews"
      data-testid="panel-interviews"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
        gap: '24px',
        alignItems: 'start',
      }}
    >
      <SectionCard
        eyebrow={t('opportunities.talent.interviews.eyebrow')}
        title={t('opportunities.talent.interviews.title')}
        note={t('opportunities.talent.interviews.note')}
      >
        {videosQuery.isPending && (
          <Box
            data-testid="talent-videos-loading"
            sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
          >
            <Skeleton variant="rounded" height={120} />
          </Box>
        )}
        {videosQuery.isError && (
          <Alert severity="warning" data-testid="talent-videos-error">
            {t('opportunities.talent.video.error')}
          </Alert>
        )}
        {videosQuery.isSuccess && videosQuery.data.items.length === 0 && (
          <EmptySectionBlock
            text={t('opportunities.talent.video.empty')}
            testId="talent-videos-empty"
          />
        )}
        {videosQuery.isSuccess && videosQuery.data.items.length > 0 && (
          <Box
            data-testid="talent-detail-videos"
            sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
          >
            {videosQuery.data.items.map((v) => (
              <VideoPlayer
                key={v.id}
                video={v}
                shortlistPublicKey={shortlistPublicKey}
                talentPublicKey={talentPublicKey}
              />
            ))}
          </Box>
        )}
      </SectionCard>
      <Box sx={{ display: 'grid', gap: '16px', position: { md: 'sticky' }, top: 20 }}>
        <ActionsCard
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          onOpenSheet={onOpenSheet}
        />
      </Box>
    </Box>
  );
}

// ============================================================================
// NOTES PANEL (reuses TalentCommentsPanel; team-only framing)
// ============================================================================

function NotesPanel({
  shortlistPublicKey,
  talentPublicKey,
  workspaceName,
  onOpenSheet,
}: {
  shortlistPublicKey: string;
  talentPublicKey: string;
  workspaceName: string;
  onOpenSheet: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      role="tabpanel"
      id="tabpanel-notes"
      aria-labelledby="tab-notes"
      data-testid="panel-notes"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
        gap: '24px',
        alignItems: 'start',
      }}
    >
      <SectionCard
        eyebrow={t('opportunities.talent.notes.eyebrow')}
        title={t('opportunities.talent.notes.title', { workspace: workspaceName })}
        note={t('opportunities.talent.notes.note')}
      >
        <TalentCommentsPanel
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
        />
      </SectionCard>
      <Box sx={{ display: 'grid', gap: '16px', position: { md: 'sticky' }, top: 20 }}>
        <ActionsCard
          shortlistPublicKey={shortlistPublicKey}
          talentPublicKey={talentPublicKey}
          onOpenSheet={onOpenSheet}
        />
      </Box>
    </Box>
  );
}

// ============================================================================
// SIDEBAR CARDS
// ============================================================================

function ActionsCard({
  shortlistPublicKey,
  talentPublicKey,
  onOpenSheet,
  onLeaveComment,
}: {
  shortlistPublicKey: string;
  talentPublicKey: string;
  onOpenSheet: () => void;
  onLeaveComment?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '20px',
        padding: '20px',
        display: 'grid',
        gap: '12px',
      }}
    >
      <Typography
        sx={{
          margin: 0,
          fontFamily: tokens.font.display,
          fontSize: 16,
          fontWeight: 600,
          letterSpacing: '-0.02em',
        }}
      >
        {t('opportunities.talent.actions.decideTitle')}
      </Typography>
      <Typography
        sx={{
          margin: 0,
          color: tokens.color.muted,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        {t('opportunities.talent.actions.decideHint')}
      </Typography>

      <TalentActionsPanel
        shortlistPublicKey={shortlistPublicKey}
        talentPublicKey={talentPublicKey}
      />

      <Box
        component="button"
        type="button"
        data-testid="actions-not-for-now"
        onClick={onOpenSheet}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px 14px',
          borderRadius: '10px',
          background: tokens.color.surface,
          color: tokens.color.ink,
          border: `1px solid ${tokens.color.line}`,
          fontFamily: 'inherit',
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': { borderColor: tokens.color.ink },
        }}
      >
        {t('opportunities.talent.actions.notForNow')}
      </Box>
      {onLeaveComment && (
        <Box
          component="button"
          type="button"
          data-testid="actions-leave-comment"
          onClick={onLeaveComment}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            background: 'none',
            border: 0,
            color: tokens.color.accent,
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {t('opportunities.talent.actions.leaveComment')} →
        </Box>
      )}
    </Box>
  );
}

function DocumentsSidePreview({ onViewAll }: { onViewAll: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '20px',
        padding: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '14px',
        }}
      >
        <Typography
          sx={{
            margin: 0,
            fontFamily: tokens.font.mono,
            textTransform: 'uppercase',
            fontSize: 10,
            letterSpacing: '0.12em',
            color: tokens.color.muted,
            fontWeight: 500,
          }}
        >
          {t('opportunities.talent.documents.title')}
        </Typography>
        <SectionLink onClick={onViewAll} testId="docs-side-view-all">
          {t('opportunities.talent.documents.viewAll')}
        </SectionLink>
      </Box>
      <EmptySectionBlock
        text={t('opportunities.talent.documents.empty')}
        testId="talent-documents-side-empty"
        compact
      />
    </Box>
  );
}

function VideosSidePreview({
  videosQuery,
  onPlay,
  onViewAll,
}: {
  videosQuery: ReturnType<typeof useTalentVideos>;
  onPlay: (v: { id: string; title: string; durationSeconds: number | null }) => void;
  onViewAll: () => void;
}) {
  const { t } = useTranslation();
  const items = videosQuery.data?.items ?? [];

  return (
    <Box
      sx={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '20px',
        padding: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '14px',
        }}
      >
        <Typography
          sx={{
            margin: 0,
            fontFamily: tokens.font.mono,
            textTransform: 'uppercase',
            fontSize: 10,
            letterSpacing: '0.12em',
            color: tokens.color.muted,
            fontWeight: 500,
          }}
        >
          {t('opportunities.talent.video.sectionTitle')}
        </Typography>
        <SectionLink onClick={onViewAll} testId="videos-side-view-all">
          {t('opportunities.talent.video.viewAll')}
        </SectionLink>
      </Box>

      {videosQuery.isPending && <Skeleton variant="rounded" height={64} />}
      {(videosQuery.isError || items.length === 0) && (
        <EmptySectionBlock
          text={t('opportunities.talent.video.empty')}
          testId="talent-videos-side-empty"
          compact
        />
      )}
      {items.length > 0 && (
        <Box sx={{ display: 'grid', gap: '8px' }}>
          {items.slice(0, 2).map((v) => (
            <Box
              key={v.id}
              component="button"
              type="button"
              data-testid={`video-side-${v.id}`}
              onClick={() =>
                onPlay({ id: v.id, title: v.title, durationSeconds: v.durationSeconds })
              }
              sx={{
                display: 'grid',
                gridTemplateColumns: '96px 1fr',
                gap: '12px',
                padding: '8px',
                border: `1px solid ${tokens.color.line}`,
                borderRadius: '12px',
                background: tokens.color.surface,
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                '&:hover': {
                  borderColor: tokens.color.ink,
                  background: tokens.color.surface2,
                },
              }}
            >
              <Box
                sx={{
                  aspectRatio: '16/10',
                  borderRadius: '8px',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, rgba(0,0,0,.04), rgba(0,0,0,.10)), repeating-linear-gradient(135deg, ${tokens.color.surface2} 0 6px, ${tokens.color.bg} 6px 12px)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: tokens.color.accent,
                    color: tokens.color.accentInk,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 11,
                  }}
                >
                  ▶
                </Box>
                {v.durationSeconds != null && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 6,
                      bottom: 6,
                      fontFamily: tokens.font.mono,
                      fontSize: 10,
                      color: tokens.color.inkInverse,
                      background: 'rgba(0,0,0,.7)',
                      padding: '1px 4px',
                      borderRadius: '3px',
                    }}
                  >
                    {formatDurationMMSS(v.durationSeconds)}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'grid', gap: '2px', alignContent: 'center', minWidth: 0 }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 500,
                    color: tokens.color.ink,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {v.title}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: tokens.font.mono,
                    fontSize: 10,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: tokens.color.muted,
                  }}
                >
                  {v.mimeType}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function KeyFactsCard({ talent, locale }: { talent: TalentDetail; locale: string }) {
  const { t } = useTranslation();
  const availabilityShort = formatAvailabilityShort(talent.availableFrom, locale, t);
  return (
    <Box
      sx={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '20px',
        padding: '20px',
      }}
    >
      <Typography
        sx={{
          margin: 0,
          mb: '14px',
          fontFamily: tokens.font.mono,
          textTransform: 'uppercase',
          fontSize: 10,
          letterSpacing: '0.12em',
          color: tokens.color.muted,
          fontWeight: 500,
        }}
      >
        {t('opportunities.talent.keyFacts.title')}
      </Typography>
      <InfoRow
        label={t('opportunities.talent.keyFacts.location')}
        value={formatLocation(talent.city, talent.country) ?? '—'}
      />
      <InfoRow label={t('opportunities.talent.keyFacts.rate')} value="—" />
      <InfoRow
        label={t('opportunities.talent.keyFacts.timeToJoin')}
        value={availabilityShort ?? '—'}
        valueClass={availabilityShort ? 'pos' : undefined}
      />
      <InfoRow label={t('opportunities.talent.keyFacts.engagement')} value="—" />
    </Box>
  );
}

function LanguagesCard() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '20px',
        padding: '20px',
      }}
    >
      <Typography
        sx={{
          margin: 0,
          mb: '14px',
          fontFamily: tokens.font.mono,
          textTransform: 'uppercase',
          fontSize: 10,
          letterSpacing: '0.12em',
          color: tokens.color.muted,
          fontWeight: 500,
        }}
      >
        {t('opportunities.talent.languages.title')}
      </Typography>
      <EmptySectionBlock
        text={t('opportunities.talent.languages.empty')}
        testId="talent-languages-empty"
        compact
      />
    </Box>
  );
}

function InfoRow({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: 'pos' | 'accent';
}) {
  const color =
    valueClass === 'pos'
      ? tokens.color.pos
      : valueClass === 'accent'
        ? tokens.color.accent
        : tokens.color.ink;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 0',
        borderTop: `1px solid ${tokens.color.lineSoft}`,
        fontSize: 13.5,
        '&:first-of-type': { borderTop: 0, paddingTop: 0 },
        '&:last-of-type': { paddingBottom: 0 },
      }}
    >
      <Box component="span" sx={{ color: tokens.color.muted }}>
        {label}
      </Box>
      <Box
        component="span"
        sx={{ color, fontWeight: 600, fontFamily: tokens.font.mono, fontSize: 13 }}
      >
        {value}
      </Box>
    </Box>
  );
}

// ============================================================================
// SHARED CARD / SECTION HELPERS
// ============================================================================

function SectionCard({
  eyebrow,
  title,
  note,
  action,
  children,
}: {
  eyebrow?: string;
  title: string;
  note?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      sx={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '20px',
        boxShadow: '0 1px 0 rgba(15,15,15,.04), 0 8px 24px -12px rgba(15,15,15,.10)',
        padding: '24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '14px',
          mb: '18px',
        }}
      >
        <Box>
          {eyebrow && (
            <Box
              sx={{
                fontFamily: tokens.font.mono,
                textTransform: 'uppercase',
                fontSize: 10,
                letterSpacing: '0.1em',
                color: tokens.color.muted,
                mb: '6px',
              }}
            >
              {eyebrow}
            </Box>
          )}
          {title && (
            <Typography
              component="h2"
              sx={{
                margin: 0,
                fontFamily: tokens.font.display,
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: tokens.color.ink,
              }}
            >
              {title}
            </Typography>
          )}
          {note && (
            <Typography
              sx={{
                color: tokens.color.muted,
                fontSize: 13,
                margin: '4px 0 0',
              }}
            >
              {note}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function SectionLink({
  children,
  onClick,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      data-testid={testId}
      sx={{
        background: 'none',
        border: 0,
        color: tokens.color.ink,
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '8px',
        '&:hover': { background: tokens.color.surface2 },
      }}
    >
      {children}
    </Box>
  );
}

function EmptySectionBlock({
  text,
  testId,
  compact,
}: {
  text: string;
  testId?: string;
  compact?: boolean;
}) {
  return (
    <Box
      data-testid={testId}
      sx={{
        padding: compact ? '12px' : '20px',
        background: tokens.color.surface2,
        border: `1px dashed ${tokens.color.line}`,
        borderRadius: '10px',
        color: tokens.color.muted,
        fontSize: 13,
        textAlign: 'center',
      }}
    >
      {text}
    </Box>
  );
}

function PrimaryButton({
  children,
  onClick,
  ...rest
}: {
  children: ReactNode;
  onClick: () => void;
  'data-testid'?: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      {...rest}
      sx={{
        height: 42,
        padding: '0 20px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: tokens.color.accent,
        color: tokens.color.accentInk,
        border: `1px solid ${tokens.color.accent}`,
        borderRadius: '10px',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        '&:hover': { opacity: 0.92 },
        '&:active': { transform: 'translateY(1px)' },
      }}
    >
      {children}
    </Box>
  );
}

function GhostButton({
  children,
  onClick,
  disabled,
  ...rest
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  'data-testid'?: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...rest}
      sx={{
        height: 38,
        padding: '0 16px',
        background: 'transparent',
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '10px',
        color: disabled ? tokens.color.muted2 : tokens.color.ink,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.6 : 1,
        '&:hover': { borderColor: disabled ? tokens.color.line : tokens.color.ink },
      }}
    >
      {children}
    </Box>
  );
}

function CheckIcon({ size = 14, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 16 16"
      fill="none"
      stroke={color}
      strokeWidth={1.8}
      sx={{ width: size, height: size, color, flexShrink: 0 }}
    >
      <path d="M3 8.5l3 3 7-7" />
    </Box>
  );
}

// ============================================================================
// VIDEO MODAL
// ============================================================================

type VideoModalState = {
  shortlistPublicKey: string;
  talentPublicKey: string;
  videoPublicKey: string;
  title: string;
  durationSeconds: number | null;
};

function VideoModal({
  state,
  onClose,
}: {
  state: VideoModalState;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Reusamos VideoPlayer dentro de un overlay para no duplicar la lógica de
  // signed URL + refresh. El placeholder del player se auto-clickea apenas montamos
  // (auto-start visual) via un small trick: el VideoPlayer ya muestra el play visible;
  // dejamos al user que dispare manualmente para evitar autoplay-policies. Simpler.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Box
      data-testid="video-modal-overlay"
      onClick={onClose}
      sx={{
        position: 'fixed',
        inset: 0,
        background: tokens.color.overlayBg,
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          background: tokens.color.ink,
          borderRadius: '18px',
          overflow: 'hidden',
          width: '100%',
          maxWidth: 880,
          boxShadow: '0 30px 80px rgba(0,0,0,.5)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            color: tokens.color.inkInverse,
          }}
        >
          <Typography
            sx={{
              margin: 0,
              fontFamily: tokens.font.display,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {state.title}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label={t('common.close')}
            data-testid="video-modal-close"
            onClick={onClose}
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              border: 0,
              background: tokens.color.darkControlBg,
              color: tokens.color.inkInverse,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              fontFamily: 'inherit',
              '&:hover': { background: tokens.color.darkControlBgHover },
            }}
          >
            ✕
          </Box>
        </Box>
        <Box sx={{ background: tokens.color.darkStage }}>
          <VideoPlayer
            video={{
              id: state.videoPublicKey,
              title: state.title,
              mimeType: 'video/mp4',
              durationSeconds: state.durationSeconds,
            }}
            shortlistPublicKey={state.shortlistPublicKey}
            talentPublicKey={state.talentPublicKey}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// DECLINE SHEET
// ============================================================================

const DECLINE_REASONS = [
  'rate',
  'seniority',
  'timezone',
  'stack',
  'role_filled',
  'other',
] as const;
type DeclineReason = (typeof DECLINE_REASONS)[number];

function DeclineSheet({
  talentName,
  onClose,
}: {
  talentName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<DeclineReason | null>(null);
  const [extra, setExtra] = useState('');
  // submitting queda placeholder porque cuando SW-141 conecte el BE va a haber
  // una mutation real con isPending — se reemplazará el setter por
  // mutation.isPending. Hoy queda false-forever (handleConfirm cierra inmediato).
  const submitting = false;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleConfirm() {
    // TODO SW-141: conectar al endpoint cuando el BE exponga decline-reason
    // (POST /api/client/.../talents/{pk}/decline con { reason, comment }). Por
    // ahora cerramos inmediatamente sin delay artificial.
    onClose();
  }

  return (
    <Box
      data-testid="decline-sheet-overlay"
      onClick={onClose}
      sx={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,15,.45)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          background: tokens.color.surface,
          borderRadius: '22px 22px 0 0',
          padding: '22px',
          width: '100%',
          maxWidth: 680,
          boxShadow: '0 -20px 60px rgba(0,0,0,.18)',
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            background: tokens.color.line,
            borderRadius: '2px',
            margin: '0 auto 18px',
          }}
        />
        <Typography
          component="h3"
          sx={{
            margin: 0,
            fontFamily: tokens.font.display,
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            mb: '4px',
          }}
        >
          {t('opportunities.talent.decline.title', { name: splitName(talentName).firstName })}
        </Typography>
        <Typography sx={{ margin: '0 0 16px', color: tokens.color.muted, fontSize: 13 }}>
          {t('opportunities.talent.decline.sub')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: '8px',
            mb: '14px',
          }}
        >
          {DECLINE_REASONS.map((r) => {
            const isSel = selected === r;
            return (
              <Box
                key={r}
                component="button"
                type="button"
                data-testid={`decline-reason-${r}`}
                onClick={() => setSelected(r)}
                sx={{
                  padding: '12px 14px',
                  border: `1px solid ${isSel ? tokens.color.accent : tokens.color.line}`,
                  borderRadius: '10px',
                  background: isSel ? tokens.color.accentSoft : tokens.color.surface,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 13.5,
                  color: tokens.color.ink2,
                  fontFamily: 'inherit',
                  fontWeight: 500,
                  '&:hover': { borderColor: tokens.color.ink },
                }}
              >
                {t(`opportunities.talent.decline.reasons.${r}`)}
              </Box>
            );
          })}
        </Box>

        {selected === 'other' && (
          <Box
            component="textarea"
            value={extra}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setExtra(e.target.value)}
            placeholder={t('opportunities.talent.decline.otherPlaceholder')}
            data-testid="decline-other-text"
            sx={{
              width: '100%',
              padding: '12px 14px',
              border: `1px solid ${tokens.color.line}`,
              borderRadius: '10px',
              background: tokens.color.surface2,
              resize: 'none',
              height: 80,
              fontFamily: 'inherit',
              fontSize: 13.5,
              mb: '12px',
            }}
          />
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
          }}
        >
          <Box
            component="button"
            type="button"
            data-testid="decline-cancel"
            onClick={onClose}
            sx={{
              height: 44,
              padding: '0 16px',
              borderRadius: '10px',
              background: tokens.color.surface,
              color: tokens.color.ink,
              border: `1px solid ${tokens.color.line}`,
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              '&:hover': { borderColor: tokens.color.ink },
            }}
          >
            {t('opportunities.talent.decline.cancel')}
          </Box>
          <Box
            component="button"
            type="button"
            data-testid="decline-confirm"
            disabled={submitting}
            onClick={handleConfirm}
            sx={{
              height: 44,
              padding: '0 16px',
              borderRadius: '10px',
              background: tokens.color.accent,
              color: tokens.color.accentInk,
              border: `1px solid ${tokens.color.accent}`,
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              '&:hover': { opacity: submitting ? 0.7 : 0.92 },
            }}
          >
            {submitting && <CircularProgress size={14} sx={{ color: 'inherit' }} />}
            {t('opportunities.talent.decline.confirm')}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// LOADING / NOT FOUND
// ============================================================================

function LoadingSkeleton() {
  return (
    <Box
      data-testid="talent-detail-loading"
      sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 360px' },
          gap: '28px',
          padding: '24px',
          background: tokens.color.surface,
          border: `1px solid ${tokens.color.line}`,
          borderRadius: '20px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '22px' }}>
          <Skeleton variant="rounded" width={96} height={96} sx={{ borderRadius: '24px' }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" sx={{ fontSize: 64 }} />
            <Skeleton variant="text" width="40%" sx={{ fontSize: 16 }} />
          </Box>
        </Box>
        <Skeleton variant="rounded" height={240} sx={{ borderRadius: '20px' }} />
      </Box>
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={120} />
    </Box>
  );
}

function NotFoundState() {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="talent-detail-not-found"
      sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
    >
      <Alert severity="info" variant="outlined">
        <Typography sx={{ fontWeight: 600, mb: '4px' }}>
          {t('opportunities.talent.notFound.title')}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>{t('opportunities.talent.notFound.message')}</Typography>
      </Alert>
    </Box>
  );
}

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

function computeInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '·';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

function splitName(name: string): { firstName: string; surname: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', surname: '' };
  if (parts.length === 1) return { firstName: parts[0]!, surname: '' };
  return { firstName: parts[0]!, surname: parts.slice(1).join(' ') };
}

function formatLocation(city: string | null, country: string | null): string | null {
  const parts = [city, country].filter((p): p is string => !!p && p.trim().length > 0);
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatDateRange(
  startIso: string,
  endIso: string | null,
  locale: string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const start = formatMonthYear(new Date(startIso), locale);
  const end = endIso
    ? formatMonthYear(new Date(endIso), locale)
    : t('opportunities.talent.experience.present');
  return `${start} — ${end}`;
}

function formatMonthYear(date: Date, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(date);
  } catch {
    return date.toISOString().slice(0, 7);
  }
}

function formatDuration(
  startIso: string,
  endIso: string | null,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const start = new Date(startIso);
  const end = endIso ? new Date(endIso) : new Date();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  const months = Math.max(0, Math.round((end.getTime() - start.getTime()) / (30.44 * 86400000)));
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  if (years === 0) {
    return t('opportunities.talent.experience.durationMonths', { count: months });
  }
  if (remMonths === 0) {
    return t('opportunities.talent.experience.durationYears', { count: years });
  }
  return t('opportunities.talent.experience.durationYearsMonths', {
    years,
    months: remMonths,
  });
}

function formatAvailabilityShort(
  availableFrom: string | null,
  locale: string,
  t: ReturnType<typeof useTranslation>['t'],
): string | null {
  if (!availableFrom) return null;
  const date = new Date(availableFrom);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getTime() <= Date.now()) {
    return t('opportunities.talent.availability.immediate');
  }
  return `${t('opportunities.talent.availability.label')}: ${formatLongDate(date, locale)}`;
}

function formatLongDate(date: Date, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function formatDurationMMSS(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
